// SNIGJ - System Configuration Page (Admin)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Settings, Hash, Shield, Users, MapPin, FileText, Save, Plus, Trash2, Eye, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { DRC_PROVINCES } from '../lib/drc-data';

// Court Levels in DRC
const COURT_LEVELS = [
  { code: 'TP', name: { fr: 'Tribunal de Paix', en: 'Peace Court' }, order: 1 },
  { code: 'TGI', name: { fr: 'Tribunal de Grande Instance', en: 'High Court' }, order: 2 },
  { code: 'CA', name: { fr: "Cour d'Appel", en: 'Court of Appeal' }, order: 3 },
  { code: 'CE', name: { fr: "Conseil d'État", en: 'Council of State' }, order: 4 },
  { code: 'CC', name: { fr: 'Cour de Cassation', en: 'Supreme Court' }, order: 5 },
  { code: 'COUR_CONST', name: { fr: 'Cour Constitutionnelle', en: 'Constitutional Court' }, order: 6 }
];

// Numbering Format Types
const NUMBERING_FORMATS = [
  { id: 'CASE', label: { fr: 'Dossiers judiciaires', en: 'Judicial Cases' }, example: 'KIN/RP/2026/00001' },
  { id: 'PRISONER', label: { fr: 'Détenus', en: 'Prisoners' }, example: 'UNJI-KIN-2026-00001' },
  { id: 'DOCUMENT', label: { fr: 'Documents', en: 'Documents' }, example: 'DOC/KIN/2026/00001' },
  { id: 'PAYMENT', label: { fr: 'Paiements', en: 'Payments' }, example: 'PAY/2026/00001' }
];

const SystemConfigPage = () => {
  const { language, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    numbering: {
      case_format: '{PROVINCE}/{TYPE}/{YEAR}/{SEQ}',
      prisoner_format: 'UNJI-{PROVINCE}-{YEAR}-{SEQ}',
      document_format: 'DOC/{PROVINCE}/{YEAR}/{SEQ}',
      payment_format: 'PAY/{YEAR}/{SEQ}',
      sequence_padding: 5,
      reset_sequence_yearly: true
    },
    access_control: {
      restrict_by_jurisdiction: true,
      restrict_by_clearance: true,
      allow_cross_jurisdiction_view: false,
      require_approval_for_transfer: true
    },
    court_levels: COURT_LEVELS,
    jurisdictions: []
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/admin/system-config');
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/admin/system-config', config);
      toast.success(language === 'fr' ? 'Configuration sauvegardée' : 'Configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de sauvegarde' : 'Save error'));
    } finally {
      setSaving(false);
    }
  };

  const formatVariables = [
    { var: '{PROVINCE}', desc: language === 'fr' ? 'Code province (ex: KIN)' : 'Province code (e.g., KIN)' },
    { var: '{TYPE}', desc: language === 'fr' ? 'Type dossier (RP, RC, RA)' : 'Case type (RP, RC, RA)' },
    { var: '{YEAR}', desc: language === 'fr' ? 'Année en cours' : 'Current year' },
    { var: '{SEQ}', desc: language === 'fr' ? 'Numéro séquentiel' : 'Sequential number' },
    { var: '{COURT}', desc: language === 'fr' ? 'Code tribunal' : 'Court code' }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 skeleton" />
          <div className="h-96 skeleton" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="system-config-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Settings className="w-8 h-8" />
              {language === 'fr' ? 'Configuration Système' : 'System Configuration'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Paramètres de numérotation et contrôle d\'accès' : 'Numbering settings and access control'}
            </p>
          </div>
          <Button onClick={saveConfig} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving ? '...' : (language === 'fr' ? 'Sauvegarder' : 'Save')}
          </Button>
        </div>

        <Tabs defaultValue="numbering" className="space-y-4">
          <TabsList>
            <TabsTrigger value="numbering" className="gap-2">
              <Hash className="w-4 h-4" />
              {language === 'fr' ? 'Numérotation' : 'Numbering'}
            </TabsTrigger>
            <TabsTrigger value="access" className="gap-2">
              <Shield className="w-4 h-4" />
              {language === 'fr' ? 'Contrôle d\'accès' : 'Access Control'}
            </TabsTrigger>
            <TabsTrigger value="courts" className="gap-2">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? 'Niveaux juridictionnels' : 'Court Levels'}
            </TabsTrigger>
          </TabsList>

          {/* Numbering Tab */}
          <TabsContent value="numbering">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Hash className="w-5 h-5" />
                  {language === 'fr' ? 'Formats de numérotation' : 'Numbering Formats'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Configurez les formats de numérotation automatique des pièces' 
                    : 'Configure automatic numbering formats for documents'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Variables Help */}
                <div className="p-4 bg-muted rounded">
                  <h4 className="font-medium mb-2">{language === 'fr' ? 'Variables disponibles' : 'Available Variables'}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {formatVariables.map((v) => (
                      <div key={v.var} className="text-sm">
                        <code className="bg-background px-1 rounded">{v.var}</code>
                        <p className="text-xs text-muted-foreground">{v.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Format Settings */}
                {NUMBERING_FORMATS.map((format) => (
                  <div key={format.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded">
                    <div className="space-y-2">
                      <Label className="font-medium">{format.label[language]}</Label>
                      <Input
                        value={config.numbering[`${format.id.toLowerCase()}_format`] || ''}
                        onChange={(e) => setConfig({
                          ...config,
                          numbering: { ...config.numbering, [`${format.id.toLowerCase()}_format`]: e.target.value }
                        })}
                        placeholder={format.example}
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">{language === 'fr' ? 'Exemple' : 'Example'}</Label>
                      <div className="p-2 bg-muted rounded font-mono text-sm">
                        {format.example}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Sequence Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Padding numéro séquentiel' : 'Sequence Padding'}</Label>
                    <Select
                      value={String(config.numbering.sequence_padding)}
                      onValueChange={(v) => setConfig({
                        ...config,
                        numbering: { ...config.numbering, sequence_padding: parseInt(v) }
                      })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[3, 4, 5, 6, 7].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n} {language === 'fr' ? 'chiffres' : 'digits'} (ex: {'0'.repeat(n-1)}1)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Réinitialiser annuellement' : 'Reset Yearly'}</Label>
                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        checked={config.numbering.reset_sequence_yearly}
                        onCheckedChange={(checked) => setConfig({
                          ...config,
                          numbering: { ...config.numbering, reset_sequence_yearly: checked }
                        })}
                      />
                      <span className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Recommencer à 1 chaque année' : 'Start from 1 each year'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                  <div className="flex items-start gap-2">
                    <Lock className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-700 dark:text-yellow-400">
                        {language === 'fr' ? 'Protection des numéros' : 'Number Protection'}
                      </p>
                      <p className="text-sm text-yellow-600 dark:text-yellow-500">
                        {language === 'fr' 
                          ? 'Les numéros générés automatiquement ne peuvent pas être modifiés par les utilisateurs normaux. Seul l\'administrateur peut effectuer des corrections.' 
                          : 'Auto-generated numbers cannot be modified by regular users. Only administrators can make corrections.'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Access Control Tab */}
          <TabsContent value="access">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {language === 'fr' ? 'Contrôle d\'accès' : 'Access Control'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Configurez les restrictions d\'accès par juridiction et niveau' 
                    : 'Configure access restrictions by jurisdiction and level'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4 p-4 border rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium">{language === 'fr' ? 'Restriction par juridiction' : 'Jurisdiction Restriction'}</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? 'Limiter l\'accès aux données de sa juridiction' : 'Limit access to own jurisdiction data'}
                        </p>
                      </div>
                      <Switch
                        checked={config.access_control.restrict_by_jurisdiction}
                        onCheckedChange={(checked) => setConfig({
                          ...config,
                          access_control: { ...config.access_control, restrict_by_jurisdiction: checked }
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 p-4 border rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium">{language === 'fr' ? 'Restriction par niveau d\'accès' : 'Clearance Restriction'}</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? 'Limiter selon la classification des documents' : 'Limit by document classification'}
                        </p>
                      </div>
                      <Switch
                        checked={config.access_control.restrict_by_clearance}
                        onCheckedChange={(checked) => setConfig({
                          ...config,
                          access_control: { ...config.access_control, restrict_by_clearance: checked }
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 p-4 border rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium">{language === 'fr' ? 'Vue inter-juridictions' : 'Cross-Jurisdiction View'}</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? 'Permettre de voir les dossiers d\'autres juridictions' : 'Allow viewing cases from other jurisdictions'}
                        </p>
                      </div>
                      <Switch
                        checked={config.access_control.allow_cross_jurisdiction_view}
                        onCheckedChange={(checked) => setConfig({
                          ...config,
                          access_control: { ...config.access_control, allow_cross_jurisdiction_view: checked }
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 p-4 border rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium">{language === 'fr' ? 'Approbation pour transfert' : 'Transfer Approval'}</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? 'Exiger une approbation pour transférer des dossiers' : 'Require approval to transfer cases'}
                        </p>
                      </div>
                      <Switch
                        checked={config.access_control.require_approval_for_transfer}
                        onCheckedChange={(checked) => setConfig({
                          ...config,
                          access_control: { ...config.access_control, require_approval_for_transfer: checked }
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Jurisdiction Hierarchy */}
                <div className="p-4 border rounded">
                  <h4 className="font-medium mb-4">{language === 'fr' ? 'Hiérarchie juridictionnelle' : 'Jurisdiction Hierarchy'}</h4>
                  <div className="space-y-2">
                    {['NATIONAL', 'PROVINCIAL', 'TERRITORIAL', 'COMMUNAL'].map((level, i) => (
                      <div key={level} className="flex items-center gap-4 p-2 rounded bg-muted/50">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                          {i + 1}
                        </div>
                        <div>
                          <p className="font-medium">{level}</p>
                          <p className="text-xs text-muted-foreground">
                            {level === 'NATIONAL' && (language === 'fr' ? 'Accès à tout le pays' : 'Access to entire country')}
                            {level === 'PROVINCIAL' && (language === 'fr' ? 'Accès à la province' : 'Access to province')}
                            {level === 'TERRITORIAL' && (language === 'fr' ? 'Accès au territoire' : 'Access to territory')}
                            {level === 'COMMUNAL' && (language === 'fr' ? 'Accès à la commune' : 'Access to commune')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Court Levels Tab */}
          <TabsContent value="courts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {language === 'fr' ? 'Niveaux juridictionnels' : 'Court Levels'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Hiérarchie des cours et tribunaux pour le traitement des dossiers' 
                    : 'Court hierarchy for case processing'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {COURT_LEVELS.map((court, index) => (
                    <div key={court.code} className="flex items-center gap-4 p-4 border rounded hover:bg-muted/50">
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{court.name[language]}</p>
                        <p className="text-sm text-muted-foreground font-mono">{court.code}</p>
                      </div>
                      {index < COURT_LEVELS.length - 1 && (
                        <div className="text-muted-foreground">→</div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {language === 'fr' 
                      ? 'Les dossiers suivent cette hiérarchie lors des appels et recours. Le Conseil d\'État traite les affaires administratives avant la Cour de Cassation.' 
                      : 'Cases follow this hierarchy during appeals. The Council of State handles administrative matters before the Supreme Court.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SystemConfigPage;
